import React, { useRef, useMemo } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon } from '../assets/icons';
import { BackgroundTreatment, SeamBridge } from '../components/shared/BackgroundTreatment';
import { useI18n } from '../i18n';

interface HeroSectionProps {
  imageSrc: string;
  imageAlt?: string;
  onGetInTouchClick?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({
  imageSrc,
  imageAlt = 'Hero background',
  onGetInTouchClick,
}) => {
  const { t } = useI18n();
  const sectionRef = useRef<HTMLDivElement>(null);

  const prefersReducedMotion = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }, []);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  // Hero content fade out as we scroll
  const heroOpacity = useTransform(scrollYProgress, [0.3, 0.7], [1, 0]);
  const heroY = useTransform(
    scrollYProgress,
    [0.3, 0.7],
    prefersReducedMotion ? [0, 0] : [0, -30]
  );
  const heroBlur = useTransform(
    scrollYProgress,
    [0.3, 0.7],
    prefersReducedMotion ? [0, 0] : [0, 4]
  );

  const filterBlur = useTransform(heroBlur, (v) => `blur(${v}px)`);

  return (
    <section
      ref={sectionRef}
      className="relative h-screen w-full bg-black"
    >
      {/* Unified Background Treatment */}
      <BackgroundTreatment
        imageSrc={imageSrc}
        imageAlt={imageAlt}
        grade="hero"
        objectPosition="center center"
        enableKenBurns={true}
      />

      {/* Seam Bridge - Bottom fade for smooth transition to Narrative */}
      <SeamBridge position="bottom" height={150} />

      {/* Hero Content */}
      <div className="relative z-10 h-full flex items-center justify-center">
        <motion.div
          className="w-full max-w-6xl mx-auto px-6 md:px-10 text-center"
          style={{
            opacity: heroOpacity,
            y: heroY,
            filter: filterBlur,
          }}
        >
          {/* Headline */}
          <motion.h1
            className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-[5.5rem] text-white leading-[1.05] mb-8 tracking-[-0.02em]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {t.hero.title}
            <br />
            <span className="text-white/60">
              {t.hero.titleHighlight}
            </span>
          </motion.h1>

          {/* Subtext */}
          <motion.p
            className="text-lg sm:text-xl md:text-2xl text-white/40 mb-12 max-w-2xl mx-auto font-light leading-relaxed"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {t.hero.subtitleBefore}
            <span className="text-[#28E675]">{t.hero.subtitleHighlight}</span>
            {t.hero.subtitleAfter}
          </motion.p>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <button
              onClick={onGetInTouchClick}
              className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-white hover:bg-white/95 text-gray-900 font-semibold text-lg transition-all duration-300 hover:scale-[1.03] shadow-2xl"
            >
              <div className="flex items-center justify-center w-9 h-9 rounded-full bg-gray-900 transition-transform group-hover:scale-110">
                <ArrowRightIcon className="w-4.5 h-4.5 text-white" />
              </div>
              <span>{t.hero.cta}</span>
            </button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HeroSection;
